import React from 'react';
import { styled } from '@linaria/react';
import Term from '@/components/Term';
import New from './New';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  label: React.ReactNode;
  labelAs?: 'label' | 'span';
  secondaryLabel?: string;
  definition?: string;
  gridArea?: string;
  cornerContents?: React.ReactNode;
  inline?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  isNew?: boolean;
  children?: React.ReactNode;
}

function ControlWrapper({
  id,
  label,
  labelAs = 'label',
  secondaryLabel,
  definition,
  // TypeScript is unhappy if we pass `undefined` to a CSS variable
  gridArea = '',
  cornerContents,
  inline,
  style = {},
  disabled,
  isNew,
  children,
  ...delegated
}: Props) {
  const Wrapper = inline ? InlineWrapper : StackedWrapper;

  const labelContents = label && (
    <>
      {label}
      {secondaryLabel && (
        <SecondaryLabel>{secondaryLabel}</SecondaryLabel>
      )}
    </>
  );

  return (
    <Wrapper
      style={{ '--grid-area': gridArea, ...style }}
      {...delegated}
    >
      <Header style={{ opacity: disabled ? 0.65 : 1 }}>
        <Label
          as={labelAs}
          htmlFor={labelAs === 'label' ? id : undefined}
        >
          {definition ? (
            <Term definition={definition}>{labelContents}</Term>
          ) : (
            labelContents
          )}
          :{isNew && <New style={{ transform: 'translateX(3px)' }} />}
        </Label>
        {/*
          Some controls, like SliderControl, will add the current
          value in this corner. Others, like ToggleControl, will
          put the actual control here.
        */}
        {cornerContents}
      </Header>
      <ChildWrapper style={{ flex: inline ? 1 : undefined }}>
        {children}
      </ChildWrapper>
    </Wrapper>
  );
}

const StackedWrapper = styled.div`
  grid-area: var(--grid-area);
  font-size: 1rem;
`;

const InlineWrapper = styled(StackedWrapper)`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
`;

const Label = styled.label`
  font-weight: var(--font-weight-medium);
  user-select: none;
`;

const SecondaryLabel = styled.span`
  display: inline-block;
  margin-left: 0.5em;
  font-size: 0.875em;
  font-weight: normal;
  opacity: 0.625;
`;

const ChildWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 1.625rem;
`;

export default ControlWrapper;
